import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import chartData from '../../data/chartData.json'
import {
  chartColors,
  barDefaults,
  mergeOptions,
  withOpacity,
  formatCurrency
} from '../../theme/chartTheme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// Calculate standard deviation
function calcStdDev(arr) {
  if (arr.length < 2) return 0
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length
  const squaredDiffs = arr.map(x => Math.pow(x - mean, 2))
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / (arr.length - 1)
  return Math.sqrt(variance)
}

function EquipmentBreakdown({ data: filterData, onBarClick }) {
  // Compute equipment stats from filtered data or use static data
  const equipment = useMemo(() => {
    if (filterData && filterData.length > 0) {
      const byEquipment = {}

      filterData.forEach(study => {
        if (!study.equipment_type || !study.efficiency_pct) return

        const type = study.equipment_type.toLowerCase().replace(/ /g, '_')

        if (!byEquipment[type]) {
          byEquipment[type] = {
            efficiencies: [],
            savings: []
          }
        }

        byEquipment[type].efficiencies.push(study.efficiency_pct)
        if (study.annual_savings) {
          byEquipment[type].savings.push(study.annual_savings)
        }
      })

      // Convert to same format as chartData with stdDev
      const result = {}
      Object.entries(byEquipment).forEach(([type, data]) => {
        if (data.efficiencies.length > 0) {
          const mean = data.efficiencies.reduce((a, b) => a + b, 0) / data.efficiencies.length
          const stdDev = calcStdDev(data.efficiencies)
          result[type] = {
            average_improvement_pct: mean,
            std_dev: stdDev,
            case_count: data.efficiencies.length,
            improvement_range: {
              min: Math.min(...data.efficiencies),
              max: Math.max(...data.efficiencies)
            },
            average_annual_savings_usd: data.savings.length > 0
              ? data.savings.reduce((a, b) => a + b, 0) / data.savings.length
              : null
          }
        }
      })

      return result
    }

    return chartData.by_equipment
  }, [filterData])

  const abbreviations = {
    'package_rtu': 'Pkg RTU',
    'dx_system': 'DX Sys',
    'split_system': 'Split Sys',
    'package_unit': 'Pkg Unit',
    'package_dx': 'Pkg DX',
    'refrigeration': 'Refrig.'
  }
  const labels = Object.keys(equipment).map(key =>
    abbreviations[key] || key.replace(/_/g, ' ').toUpperCase()
  )

  const equipmentValues = Object.values(equipment)
  const avgImprovement = equipmentValues.map(eq => eq.average_improvement_pct)
  const caseCounts = equipmentValues.map(eq => eq.case_count)

  // Calculate error bar data (mean - stdDev to mean + stdDev) as floating bars
  const errorBars = equipmentValues.map(eq => {
    const mean = eq.average_improvement_pct
    const stdDev = eq.std_dev || 0
    return [Math.max(0, mean - stdDev), mean + stdDev]
  })

  const data = {
    labels,
    datasets: [
      {
        label: '± 1σ Range',
        data: errorBars,
        backgroundColor: withOpacity(chartColors.primary.main, 0.2),
        borderColor: withOpacity(chartColors.primary.dark, 0.4),
        borderWidth: 1,
        borderRadius: 3,
        yAxisID: 'y',
        barPercentage: 0.3,
        order: 2
      },
      {
        label: 'Mean Improvement %',
        data: avgImprovement,
        backgroundColor: chartColors.primary.main,
        hoverBackgroundColor: chartColors.primary.light,
        borderColor: chartColors.primary.dark,
        borderWidth: 0,
        borderRadius: barDefaults.borderRadius,
        yAxisID: 'y',
        barPercentage: 0.6,
        order: 1
      },
      {
        label: 'Case Count (n)',
        data: caseCounts,
        backgroundColor: chartColors.info,
        hoverBackgroundColor: withOpacity(chartColors.info, 0.8),
        borderColor: chartColors.secondary.main,
        borderWidth: 0,
        borderRadius: barDefaults.borderRadius,
        yAxisID: 'y1',
        barPercentage: 0.4,
        order: 3
      }
    ]
  }

  const equipmentKeys = Object.keys(equipment)

  const options = mergeOptions({
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index
        const equipmentKey = equipmentKeys[index]
        onBarClick('equipment', equipmentKey)
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11,
            weight: 500
          },
          color: chartColors.textSecondary
        }
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            return context[0].label
          },
          afterBody: (context) => {
            const key = equipmentKeys[context[0].dataIndex]
            const eq = equipment[key]
            const stdDev = eq.std_dev || 0
            return [
              '',
              `Mean: ${eq.average_improvement_pct.toFixed(1)}% ± ${stdDev.toFixed(1)}%`,
              `Range: ${eq.improvement_range.min.toFixed(1)}% – ${eq.improvement_range.max.toFixed(1)}%`,
              `Studies: n = ${eq.case_count}`,
              `Avg Savings: ${eq.average_annual_savings_usd ? formatCurrency(eq.average_annual_savings_usd) : 'N/A'}`
            ]
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Improvement % (± 1σ)',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        min: 0,
        max: 55,
        grid: {
          color: chartColors.grid
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: {
          display: true,
          text: 'Case Count',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          drawOnChartArea: false
        },
        min: 0,
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 10,
            weight: 500
          },
          color: chartColors.textSecondary
        }
      }
    }
  })

  return <Bar data={data} options={options} />
}

export default EquipmentBreakdown
